/* LEGAL-NOTICE
   © Copyright 2018 - Quid Informatica S.p.A. - All Rights Reserved
   https://quidinfo.it/qinetic/licence.html - "Quid Consumer Lending Platform"
   Deposito SIAE n. 012702 del 26 Luglio 2018
LEGAL-NOTICE */
import { Injectable } from '@angular/core';
import { ErrorDisplay, NotificationSkipModel } from '@core/models/error';
import { ToastController, ModalController } from '@ionic/angular';
import { AppService } from '@services/app/app.service';
import { LabelsService } from '@services/labels/labels.service';
import { HelpService } from '@services/help/help.service';
import { UiService } from '../ui/ui.service';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { ErrorButtonModel, UiErrorModel } from '@core/models';
import { ApiErrorDecoder } from '@core/utils';

export interface RetryInstanceModel {
  id: string;
  registeredAt: number;
  notifier$: BehaviorSubject<boolean>;
}

@Injectable({
  providedIn: 'root'
})
export class ErrorService {
  /**
   * configurazione per lo skip della notifica di errore
   */
  private _notificationSkip: NotificationSkipModel;

  /**
   * oggetto di errore
   */
  private error: UiErrorModel;

  /**
   * lista di retry instances
   */
  private retryInstances: RetryInstanceModel[] = [];

  /**
   * accesso in lettura della configurazione per lo skip della notifica di errore
   */
  get notificationSkip(): NotificationSkipModel {
    return this._notificationSkip && { ...this._notificationSkip };
  }

  constructor(
    private toastController: ToastController,
    private modalController: ModalController,
    private appService: AppService,
    private uiService: UiService,
    private labelsService: LabelsService,
    private helpService: HelpService
  ) {}

  /**
   * imposta azione da eseguire sull'eventuale retry.
   * In caso di successo, eseguire sempre il cleanRetryWith
   */
  retryWith(action: () => any): string {
    const id = this.createRetryInstance();

    this.getRetryWhenOccur(id).subscribe(runAction => {
      if (runAction) {
        action();
      }
    });

    return id;
  }

  /**
   * rimuove l'azione di retry imposta sull'id
   */
  cleanRetryWith(id: string): void {
    this.removeRetryInstance(id);
  }

  /**
   * mostra l'errore partendo dal decoder
   */
  async showErrorFromApi(decoder: ApiErrorDecoder): Promise<void> {
    return this.showError({
      message: decoder.getMessage(),
      id: decoder.getPartialUuid(),
      code: decoder.getCode()
    });
  }

  /**
   * mostra l'errore impostato
   */
  async showError(error: UiErrorModel): Promise<void> {
    this.setError(error);

    if (!this.isErrorOccured()) {
      return;
    }

    const mode = this.error.displayMode || this.getErrorDisplayMode();

    if (mode === 'complete') {
      await this.showErrorAsCompleteToast();
    } else if (mode === 'base') {
      await this.showErrorAsBaseToast();
    } else if (mode === 'page') {
      await this.showErrorAsPage();
    }

    this.cleanError();
  }

  /**
   * non mostra il messaggio di errore per la prossima chiamata
   */
  skipNextNotification(code?: string): void {
    if (code) {
      this._notificationSkip = {
        when: 'code',
        code
      };
    } else {
      this._notificationSkip = {
        when: 'always'
      };
    }
  }

  /**
   * ripristina la configurazione per la visualizzazione dell'errore per le prossime chiamate
   */
  doNextNotification(): void {
    this._notificationSkip = { when: 'never' };
  }

  /**
   * visualizza toast message avanzato con codice errore
   */
  private async showErrorAsCompleteToast(): Promise<void> {
    const toast = await this.toastController.create({
      header: this.labelsService.getLabel(this.getErrorMessage()),
      message: `Error code: ${this.getErrorId()}`,
      position: 'top',
      color: 'danger',
      duration: 8000,
      cssClass: 'complete-error-toast',
      buttons: [
        {
          icon: 'close',
          role: 'cancel',
          handler: () => toast.dismiss()
        }
      ]
    });

    await toast.present();
  }

  /**
   * visualizza toast message di base
   */
  private async showErrorAsBaseToast(): Promise<void> {
    await this.uiService.showErrorToast(this.getErrorMessage());
  }

  /**
   * visualizza errore in modale dedicata
   */
  private async showErrorAsPage(): Promise<void> {
    const res = await this.uiService.showErrorModal(
      {
        title: 'wrn.generic_title',
        instructions: 'lbl.what_you_can_do',
        message: this.getErrorMessage(),
        id: this.getErrorId(),
        code: this.getErrorCode(),
        buttons: this.getButtons(),
        icon: this.getErrorIcon()
      },
      this.modalController
    );

    if (res && res.action === 'retry') {
      this.doRetry();
    } else {
      this.removeRetry();
    }
  }

  /**
   * definisce la lista di bottoni da visualizzare nelle modale di errore
   */
  private getButtons(): ErrorButtonModel[] {
    const list: ErrorButtonModel[] = [];

    if (this.isRetryButtonEnabled()) {
      list.push(this.getRetryButton());
    }

    if (this.isAssistanceButtonEnabled()) {
      list.push(this.getAssistanceButton());
    }

    return list;
  }

  /**
   * definisce il bottone di retry
   */
  private getRetryButton(): ErrorButtonModel {
    const baseLabel = 'btn.error_retry';
    const advLabel = 'btn.error_retry_' + this.getErrorCode();

    return {
      text: this.labelsService.doesLabelExists(advLabel) ? advLabel : baseLabel,
      action: 'retry',
      appearance:
        this.appService.getConfig().generic.retryBtnAppearance || 'outline'
    };
  }

  /**
   * definisce il bottone di assistenza
   */
  private getAssistanceButton(): ErrorButtonModel {
    return {
      text: 'btn.error_ask_assistance',
      action: 'stop',
      appearance:
        this.appService.getConfig().generic.assistanceBtnAppearance || 'solid',
      preventClosing: true,
      handler: () => this.helpService.openLink('live_chat')
    };
  }

  /**
   * stabilisce se il retry button è abilitato
   */
  private isRetryButtonEnabled(): boolean {
    if (typeof this.error.retryBtnVisible === 'boolean') {
      return this.error.retryBtnVisible;
    } else {
      return !this.isErrorBlocker();
    }
  }

  /**
   * stabilisce se il bottone di assistenza è abilitato
   */
  private isAssistanceButtonEnabled(): boolean {
    if (typeof this.error.assistanceBtnVisible === 'boolean') {
      return this.error.assistanceBtnVisible;
    } else {
      return this.helpService.isLinkActive('live_chat');
    }
  }

  /**
   * stabilisce se è presente un errore
   */
  private isErrorOccured(): boolean {
    return !!this.error && !!this.error.message;
  }

  /**
   * stabilisce se l'errore è bloccante
   */
  private isErrorBlocker(): boolean {
    const code = this.getAdaptedErrorCode();

    return (
      this.getBlockingErrorCodes().includes(code) && !this.isErrorWarning()
    );
  }

  /**
   * stabilisce se l'errore è un warning
   */
  private isErrorWarning(): boolean {
    const code = this.getAdaptedErrorCode();

    return this.getWarningErrorCodes().includes(code);
  }

  /**
   * stabilisce se il codice è tra quelli previsti
   */
  private isHandledErrorCode(): boolean {
    return [
      ...this.getBlockingErrorCodes(),
      ...this.getWarningErrorCodes()
    ].includes(this.getErrorCode());
  }

  /**
   * restituisce il codice dell'errore adattato in base a quelli gestiti
   */
  private getAdaptedErrorCode(): string {
    if (!this.isHandledErrorCode() && !!this.getUnhandledErrorCode()) {
      return this.getUnhandledErrorCode();
    } else {
      return this.getErrorCode();
    }
  }

  /**
   * restituisce il codice dell'errore
   */
  private getErrorCode(): string {
    return this.error && this.error.code;
  }

  /**
   * restituisce l'id dell'errore
   */
  private getErrorId(): string {
    return this.error && this.error.id;
  }

  /**
   * restituisce il messaggio dell'errore
   */
  private getErrorMessage(): string {
    return this.error && this.error.message;
  }

  /**
   * definisce l'icona in base al codice dell'errore
   */
  private getErrorIcon(): string {
    const code = this.getAdaptedErrorCode();

    return (
      (this.appService.getConfig().generic.errorIcons &&
        this.appService.getConfig().generic.errorIcons[code]) ||
      'exlamation-point'
    );
  }

  /**
   * restituisce la lista di codici di errore configurati come bloccanti
   */
  private getBlockingErrorCodes(): string[] {
    return (
      this.appService.getConfig().generic.blockingErrorCodes || []
    ).slice();
  }

  /**
   * restituisce la lista di codice di errore configurati come blocking
   */
  private getWarningErrorCodes(): string[] {
    return (
      this.appService.getConfig().generic.warningErrorCodes || []
    ).slice();
  }

  /**
   * restituisce il codice di errore per gli errori non previsti
   */
  private getUnhandledErrorCode(): string {
    return this.appService.getConfig().generic.unhandledErrorCodeAs;
  }

  /**
   * modalità di visualizzazione dell'errore
   */
  private getErrorDisplayMode(): ErrorDisplay {
    return this.appService.getConfig().generic.errorDisplay;
  }

  /**
   * esegue il retry
   */
  private doRetry(): void {
    const firstRetry = this.getFirstPendingRetryInstance();

    if (!!firstRetry) {
      this.notifyRetryInstance(firstRetry.id);
      this.removeRetryInstance(firstRetry.id);
    }
  }

  /**
   * rimuove il retry
   */
  private removeRetry(): void {
    const firstRetry = this.getFirstPendingRetryInstance();

    if (!!firstRetry) {
      this.notifyRetryInstance(firstRetry.id, false);
      this.removeRetryInstance(firstRetry.id);
    }
  }

  /**
   * restituisce valore quando il retry innescato corrisponde
   * all'id richiesto
   */
  private getRetryWhenOccur(id: string): Observable<boolean> {
    const instance = this.retryInstances.find(x => x.id === id);

    if (!instance) {
      return of(false);
    }

    return instance.notifier$.asObservable().pipe(
      filter(x => x === true),
      take(1)
    );
  }

  /**
   * restituisce la prima instance di retry che risulta pending
   */
  private getFirstPendingRetryInstance(): RetryInstanceModel {
    return this.retryInstances
      .filter(x => x.notifier$.getValue() !== true)
      .sort((a, b) => a.registeredAt - b.registeredAt)[0];
  }

  /**
   * crea nuovo retry instance
   */
  private createRetryInstance(): string {
    const now = new Date().getTime();
    const id = `err-${now}`;

    this.retryInstances.push({
      id,
      registeredAt: now,
      notifier$: new BehaviorSubject(false)
    });

    return id;
  }

  /**
   * notifica il retry instance
   */
  private notifyRetryInstance(id: string, value: boolean = true): void {
    const instance = this.retryInstances.find(x => x.id === id);

    if (instance) {
      instance.notifier$.next(value);
    }
  }

  /**
   * rimuove il retry instance
   */
  private removeRetryInstance(id: string): void {
    const index = this.retryInstances.findIndex(x => x.id === id);

    if (this.retryInstances[index]) {
      this.retryInstances[index].notifier$.complete();
      this.retryInstances.splice(index, 1);
    }
  }

  /**
   * imposta l'errore
   */
  private setError(error: UiErrorModel): void {
    if (!error || !error.message) {
      return this.cleanError();
    }

    this.error = {
      message: error.message,
      id: error.id || null,
      code: error.code || null,
      retryBtnVisible:
        typeof error.retryBtnVisible === 'boolean'
          ? error.retryBtnVisible
          : undefined,
      assistanceBtnVisible:
        typeof error.assistanceBtnVisible === 'boolean'
          ? error.assistanceBtnVisible
          : undefined,
      displayMode: error.displayMode || undefined
    };
  }

  /**
   * cancella l'errore impostato
   */
  private cleanError(): void {
    this.error = undefined;
  }
}
